import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { REG_HUB_COMMON_CONFIG, RegHubCommonConfig } from '../../reg-hub-common-config';
import { NaicsCode } from '../../interfaces/naics/naics-code';
import { SelectOption } from '../../interfaces/select-option';

/**
 * Read-only access to the NAICS 2022 classification for tiered drill-down.
 *
 * Typical flow: getSectors() -> user picks -> getChildren(code) -> repeat while
 * `hasChildren` is true; when `hasChildren` is false (level 5) the selection is final.
 * getPath(code) yields the breadcrumb, and search(query) backs a "find my industry" box.
 */
@Injectable({
  providedIn: 'root',
})
export class NaicsRepositoryService {

  protected _baseLink: string;

  constructor(
    private http: HttpClient,
    @Inject(REG_HUB_COMMON_CONFIG) private config: RegHubCommonConfig) {
    this._baseLink = `${this.config.rootUrl}/naics`;
  }

  /** The 20 top-level sectors — the entry point for drill-down. */
  public getSectors(): Observable<NaicsCode[]> {
    return this.http.get<NaicsCode[]>(`${this._baseLink}/sectors`);
  }

  /** The direct children of a code (the next tier down). */
  public getChildren(code: string): Observable<NaicsCode[]> {
    return this.http.get<NaicsCode[]>(`${this._baseLink}/${encodeURIComponent(code)}/children`);
  }

  /** The breadcrumb from the top-level sector down to and including the code. */
  public getPath(code: string): Observable<NaicsCode[]> {
    return this.http.get<NaicsCode[]>(`${this._baseLink}/${encodeURIComponent(code)}/path`);
  }

  /** Search NAICS titles (case-insensitive) for the "find my industry" flow. */
  public search(query: string): Observable<NaicsCode[]> {
    const params = new HttpParams().set('q', query);
    return this.http.get<NaicsCode[]>(`${this._baseLink}/search`, { params });
  }

  /** A single NAICS node by code. */
  public getByCode(code: string): Observable<NaicsCode> {
    return this.http.get<NaicsCode>(`${this._baseLink}/${encodeURIComponent(code)}`);
  }

  /** The sectors mapped to select options, for dropdown-driven drill-down. */
  public getSectorsAsSelectOptions(french: boolean = false): Observable<SelectOption[]> {
    return this.getSectors().pipe(map(codes => codes.map(code => this.mapNaicsCodeToSelectOption(code, french))));
  }

  /** The children of a code mapped to select options, for dropdown-driven drill-down. */
  public getChildrenAsSelectOptions(code: string, french: boolean = false): Observable<SelectOption[]> {
    return this.getChildren(code).pipe(map(codes => codes.map(c => this.mapNaicsCodeToSelectOption(c, french))));
  }

  /** The title in the requested language, falling back to English when no French title exists. */
  public getTitle(naicsCode: NaicsCode, french: boolean = false): string {
    return french ? (naicsCode.title.fr || naicsCode.title.en) : naicsCode.title.en;
  }

  private mapNaicsCodeToSelectOption(naicsCode: NaicsCode, french: boolean = false): SelectOption {
    return { label: `${naicsCode.code} - ${this.getTitle(naicsCode, french)}`, value: naicsCode.code };
  }
}
